// src/pages/markets/board/api/useEntryMarkers.ts
//
// The dots on a single-market card's price line. Same request (and cache) as
// the conviction row's hover panel, but this one is eager: the dots are visible
// without hovering, so a visible card with tracked traders fetches on mount.

import { useMemo } from 'react';
import { useTraders } from './useTraders';
import { buildEntryMarkers, type EntryMarkerSet } from '../utils/entryMarkers';

const EMPTY: EntryMarkerSet = { markers: [], earlier: 0, hidden: 0 };

export function useEntryMarkers(
    marketId: string | null,
    group: string | null,
    enabled: boolean,
    opts: { nowMs: number; windowStartMs: number | null }
): EntryMarkerSet & { isLoading: boolean } {
    const traders = useTraders(marketId, group, enabled);
    const rows = traders.data?.traders;

    const set = useMemo(() => {
        // No window means no honest x for anything — never treat that as "all in".
        if (!rows || rows.length === 0 || opts.windowStartMs === null)
            return EMPTY;
        return buildEntryMarkers(rows, {
            nowMs: opts.nowMs,
            windowStartMs: opts.windowStartMs,
        });
    }, [rows, opts.nowMs, opts.windowStartMs]);

    return { ...set, isLoading: traders.isLoading };
}
